/// <reference lib="dom" />
import type { Point, EditorConfig } from '../types/cv-editor.js';

/**
 * CvEditor
 * 
 * High-performance, accessible canvas editor for CV measurements.
 * Handles "Object-Fit: Contain" logic to prevent image distortion.
 */
export class CvEditor {
  private readonly config: EditorConfig = {
    activeColor: '#D40032',
    inactiveColor: '#FFFFFF',
    magnifierZoom: 2,
    magnifierSize: 120,
    minTouchTarget: 30, // Physical px radius
  };

  private readonly overlay: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly magContainer: HTMLElement;
  private readonly magCanvas: HTMLCanvasElement;
  private readonly magCtx: CanvasRenderingContext2D;
  private readonly announcer: HTMLElement | null;

  private activePointIndex = -1;
  private points: Point[] = [];
  private image: HTMLImageElement | null = null;
  
  // Layout State (for Aspect Ratio correction)
  private readonly layout = {
    offsetX: 0,
    offsetY: 0,
    renderWidth: 0,
    renderHeight: 0,
    scale: 1 // Canvas Px / Image Px
  };

  // Observers
  private readonly resizeObserver: ResizeObserver;
  
  // Reactive State
  private readonly state: { width: number; height: number };

  constructor(overlayId: string) {
    const overlay = document.getElementById(overlayId);
    if (!overlay) throw new Error(`Overlay ${overlayId} not found`);
    this.overlay = overlay;

    const canvas = this.overlay.querySelector<HTMLCanvasElement>('#cv-editor-canvas');
    const magContainer = this.overlay.querySelector<HTMLElement>('#cv-mag');
    const magCanvas = this.overlay.querySelector<HTMLCanvasElement>('#cv-mag-canvas');

    if (!canvas || !magContainer || !magCanvas) {
      throw new Error('CvEditor: Required DOM elements not found');
    }

    this.canvas = canvas;
    this.announcer = this.overlay.querySelector('#cv-announcer');
    const ctx = this.canvas.getContext('2d', { alpha: false, desynchronized: true });
    if (!ctx) throw new Error('CvEditor: Could not get 2D context');
    this.ctx = ctx;
    
    this.magContainer = magContainer;
    this.magCanvas = magCanvas;
    const magCtx = this.magCanvas.getContext('2d', { alpha: false });
    if (!magCtx) throw new Error('CvEditor: Could not get Magnifier 2D context');
    this.magCtx = magCtx;

    // Observers
    this.resizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });
    
    // Initialize
    this.state = this.createReactiveState({ width: 0, height: 0 });
    this.bindEvents();
    this.initFocusTrap();
  }

  /**
   * Proxy for efficient DOM updates. 
   * Updates are batched via requestAnimationFrame in the loop, 
   * but text updates happen here.
   */
  private createReactiveState(initial: { width: number; height: number }): { width: number; height: number } {
    return new Proxy(initial, {
      set: (target, prop, value: unknown) => {
        if (typeof prop === 'string' && (prop === 'width' || prop === 'height')) {
          const numValue = typeof value === 'number' ? value : 0;
          target[prop] = numValue;
          // Optimization: direct DOM manipulation for text only
          const el = this.overlay.querySelector(`[data-bind="${prop}"]`);
          if (el) {
            el.textContent = Math.round(numValue).toString();
          }
        }
        return true;
      }
    });
  }

  public open(imageUrl: string, initialPoints?: Point[]) {
    this.overlay.classList.remove('invisible', 'opacity-0');
    this.overlay.setAttribute('aria-hidden', 'false');
    
    this.image = new Image();
    this.image.onload = () => {
      this.resizeCanvas();
      this.points = initialPoints?.length === 4 
        ? initialPoints 
        : this.getDefaultPoints();
        
      this.draw();
      this.updateMeasurements();
      this.resizeObserver.observe(this.overlay);
    };
    this.image.src = imageUrl;
    
    // A11y: Trap focus
    setTimeout(() => {
      this.canvas.focus();
    }, 50);
  }

  public close() {
    this.overlay.classList.add('invisible', 'opacity-0');
    this.overlay.setAttribute('aria-hidden', 'true');
    this.resizeObserver.disconnect();
    this.image = null; // Free memory
  }

  /**
   * Handles "Object-Fit: Contain" logic.
   * Calculates offsets so the image is centered and not distorted.
   */
  private resizeCanvas() {
    const container = this.overlay.querySelector('#cv-canvas-container');
    if (!container || !this.image) return;

    // 1. Set internal resolution to match physical pixels (Sharpness)
    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    
    // CSS scale back down
    this.canvas.style.width = `${String(rect.width)}px`;
    this.canvas.style.height = `${String(rect.height)}px`;
    
    // CRITICAL FIX: Reset transform before scaling to avoid compounding
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.scale(dpr, dpr);

    // 2. Calculate Layout (Letterboxing)
    const canvasRatio = rect.width / rect.height;
    const imgRatio = this.image.naturalWidth / this.image.naturalHeight;

    if (canvasRatio > imgRatio) {
      // Canvas is wider than image -> Pillarbox
      this.layout.renderHeight = rect.height;
      this.layout.renderWidth = rect.height * imgRatio;
      this.layout.offsetX = (rect.width - this.layout.renderWidth) / 2;
      this.layout.offsetY = 0;
    } else {
      // Canvas is taller than image -> Letterbox
      this.layout.renderWidth = rect.width;
      this.layout.renderHeight = rect.width / imgRatio;
      this.layout.offsetX = 0;
      this.layout.offsetY = (rect.height - this.layout.renderHeight) / 2;
    }

    this.layout.scale = this.layout.renderWidth / this.image.naturalWidth;
    this.draw();
  }

  private handleResize() {
    requestAnimationFrame(() => {
      this.resizeCanvas();
    });
  }

  /**
   * Main Render Loop (O(1))
   */
  private draw() {
    if (!this.image) return;

    // Logical pixels
    const { width, height } = this.canvas.getBoundingClientRect(); 

    this.ctx.clearRect(0, 0, width, height);

    // 1. Draw Image (Centered & Scaled)
    this.ctx.drawImage(
      this.image, 
      this.layout.offsetX, 
      this.layout.offsetY, 
      this.layout.renderWidth, 
      this.layout.renderHeight
    );

    // 2. Dark Overlay Mask with Hole
    this.ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    this.ctx.beginPath();
    this.ctx.rect(0, 0, width, height);
    
    // The "Hole" (Polygon)
    const points = this.points;
    if (points.length > 0) {
        const p0 = this.toScreen(points[0]);
        this.ctx.moveTo(p0.x, p0.y);
        for (let i = 1; i < points.length; i++) {
            const p = this.toScreen(points[i]);
            this.ctx.lineTo(p.x, p.y);
        }
    }
    this.ctx.closePath();
    this.ctx.fill('evenodd');

    // 3. Lines & Handles
    this.ctx.lineWidth = 2;
    this.ctx.strokeStyle = this.config.activeColor;
    
    // Lines
    this.ctx.beginPath();
    this.points.forEach((rawP, i) => {
      const p = this.toScreen(rawP);
      if (i === 0) this.ctx.moveTo(p.x, p.y);
      else this.ctx.lineTo(p.x, p.y);
    });
    this.ctx.closePath();
    this.ctx.stroke();

    // Handles
    this.points.forEach((rawP, i) => {
      const p = this.toScreen(rawP);
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, 8, 0, Math.PI * 2);
      this.ctx.fillStyle = i === this.activePointIndex ? this.config.activeColor : this.config.inactiveColor;
      this.ctx.fill();
      this.ctx.stroke();
    });
  }

  /**
   * Transforms: Image Coordinate Space <-> Screen Coordinate Space
   */
  private toScreen(point: Point): Point {
    return {
      x: this.layout.offsetX + (point.x * this.layout.scale),
      y: this.layout.offsetY + (point.y * this.layout.scale)
    };
  }

  private toImage(x: number, y: number): Point {
    return {
      x: (x - this.layout.offsetX) / this.layout.scale,
      y: (y - this.layout.offsetY) / this.layout.scale
    };
  }

  private updateMagnifier(screenX: number, screenY: number) {
    if (!this.image) return;
    
    // Calculate source coordinates on the original image
    const imgPoint = this.toImage(screenX, screenY);
    
    // Clamp to image bounds
    const srcX = Math.max(0, Math.min(this.image.naturalWidth, imgPoint.x));
    const srcY = Math.max(0, Math.min(this.image.naturalHeight, imgPoint.y));

    const zoom = this.config.magnifierZoom;
    const size = this.config.magnifierSize;

    this.magCtx.clearRect(0, 0, size, size);
    
    // Draw source slice to magnifier
    this.magCtx.drawImage(
      this.image,
      srcX - (size / (2 * zoom)), 
      srcY - (size / (2 * zoom)),
      size / zoom, size / zoom,
      0, 0, size, size
    );

    // Crosshair
    this.magCtx.strokeStyle = 'rgba(255,255,255,0.5)';
    this.magCtx.lineWidth = 1;
    this.magCtx.beginPath();
    this.magCtx.moveTo(size/2, 0); this.magCtx.lineTo(size/2, size);
    this.magCtx.moveTo(0, size/2); this.magCtx.lineTo(size, size/2);
    this.magCtx.stroke();

    // Position UI
    this.magContainer.style.transform = `translate(${String(screenX - size / 2)}px, ${String(screenY - size - 20)}px)`;
    this.magContainer.classList.remove('opacity-0');
  }

  private bindEvents() {
    // Pointer Events (Unified Touch/Mouse)
    this.canvas.addEventListener('pointerdown', e => {
      const rect = this.canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Hit Test in Screen Space
      const hitIdx = this.points.findIndex(p => {
        const screenP = this.toScreen(p);
        const dist = Math.hypot(screenP.x - x, screenP.y - y);
        return dist < this.config.minTouchTarget;
      });

      if (hitIdx !== -1) {
        this.activePointIndex = hitIdx;
        this.canvas.setPointerCapture(e.pointerId);
        
        const onMove = (moveEv: PointerEvent) => {
          const moveRect = this.canvas.getBoundingClientRect();
          const moveX = moveEv.clientX - moveRect.left;
          const moveY = moveEv.clientY - moveRect.top;
          
          // Convert back to Image Space for storage
          this.points[this.activePointIndex] = this.toImage(moveX, moveY);
          
          requestAnimationFrame(() => {
            this.draw();
            this.updateMagnifier(moveX, moveY);
            this.updateMeasurements();
          });
        };

        const onUp = () => {
          this.canvas.removeEventListener('pointermove', onMove as unknown as EventListener);
          this.canvas.removeEventListener('pointerup', onUp as unknown as EventListener);
          this.magContainer.classList.add('opacity-0');
        };

        this.canvas.addEventListener('pointermove', onMove as unknown as EventListener);
        this.canvas.addEventListener('pointerup', onUp as unknown as EventListener);
        this.draw();
      }
    });

    // Keyboard Accessibility
    this.canvas.addEventListener('keydown', (e) => {
      if (this.activePointIndex === -1) this.activePointIndex = 0;
      
      const imgPoint = this.points[this.activePointIndex];
      // Move by 1/100th of image size or 10x with Shift
      const step = (e.shiftKey ? 10 : 1) / this.layout.scale; 
      
      let moved = false;
      let direction = '';

      switch(e.key) {
        case 'ArrowUp': 
          imgPoint.y -= step; 
          moved = true; 
          direction = 'omhoog';
          break;
        case 'ArrowDown': 
          imgPoint.y += step; 
          moved = true; 
          direction = 'omlaag';
          break;
        case 'ArrowLeft': 
          imgPoint.x -= step; 
          moved = true; 
          direction = 'naar links';
          break;
        case 'ArrowRight': 
          imgPoint.x += step; 
          moved = true; 
          direction = 'naar rechts';
          break;
        case 'Tab': 
          e.preventDefault();
          this.activePointIndex = (this.activePointIndex + 1) % this.points.length;
          this.announce(`Punt ${String(this.activePointIndex + 1)} geselecteerd`);
          break;
        default: return;
      }
      
      if (moved) {
        this.announce(`Punt ${String(this.activePointIndex + 1)} verplaatst ${direction}`);
        this.draw();
        this.updateMeasurements();
      }
    });

    // Close Hooks
    const closeBtn = this.overlay.querySelector('#cv-editor-close-top');
    const cancelBtn = this.overlay.querySelector('#cv-editor-cancel');
    const saveBtn = this.overlay.querySelector('#cv-editor-save');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        this.close();
      });
    }
    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        this.close();
      });
    }
    
    if (saveBtn) {
        saveBtn.addEventListener('click', () => {
            // Dispatch event for parent to listen to
            const event = new CustomEvent('cv-save', { 
                detail: { 
                    points: this.points,
                    dimensions: this.state 
                } 
            });
            this.overlay.dispatchEvent(event);
            this.close();
        });
    }
  }

  private updateMeasurements() {
    // Distance in Image Pixels
    const p = this.points;
    if (p.length < 4) return;
    
    const widthPx = Math.hypot(p[1].x - p[0].x, p[1].y - p[0].y);
    const heightPx = Math.hypot(p[3].x - p[0].x, p[3].y - p[0].y);
    
    // NOTE: This returns Image Pixels. 
    this.state.width = widthPx; 
    this.state.height = heightPx;
  }

  private getDefaultPoints(): Point[] {
    if (!this.image) return [];
    const w = this.image.naturalWidth;
    const h = this.image.naturalHeight;
    // Default 10% padding box
    return [
      { x: w * 0.1, y: h * 0.1 }, 
      { x: w * 0.9, y: h * 0.1 },
      { x: w * 0.9, y: h * 0.9 }, 
      { x: w * 0.1, y: h * 0.9 }
    ];
  }

  private announce(message: string) {
    if (this.announcer) {
      this.announcer.textContent = message;
    }
  }

  private initFocusTrap() {
    const focusable = this.overlay.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    this.overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
      if (e.key === 'Escape') {
        this.close();
      }
    });
  }
}

// Global expose for app.js
(globalThis as unknown as { CvEditor: typeof CvEditor }).CvEditor = CvEditor;
